import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Box, ButtonBase, ButtonBaseProps, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { RADIUS_SURFACE, SURFACE_SHADOW, surfaceSx } from '../../styles/tokens';

export const DASHBOARD_RADIUS = RADIUS_SURFACE;
export const dashboardCardSx = surfaceSx;
const CARD_SHADOW = SURFACE_SHADOW;

type TileTone = 'primary' | 'dark' | 'success';

const TONES: Record<TileTone, { background: string; hoverShadow: string }> = {
  primary: {
    background: 'linear-gradient(135deg, #C62327 0%, #A71A1E 100%)',
    hoverShadow: '0 18px 32px -14px rgba(198, 35, 39, 0.6)'
  },
  success: {
    background: 'linear-gradient(135deg, #22994A 0%, #187530 100%)',
    hoverShadow: '0 18px 32px -14px rgba(30, 138, 60, 0.6)'
  },
  dark: {
    background: 'linear-gradient(135deg, #2C2C34 0%, #1C1C22 100%)',
    hoverShadow: '0 18px 32px -14px rgba(28, 28, 34, 0.6)'
  }
};

interface DashboardActionTileProps extends Omit<ButtonBaseProps, 'title'> {
  title: string;
  description: string;
  icon: ReactNode;
  tone: TileTone;
}

/** Whole tile is one big button, so the primary actions are easy to hit. */
export const DashboardActionTile = ({ title, description, icon, tone, sx, ...props }: DashboardActionTileProps) => (
  <ButtonBase
    focusRipple={false}
    {...props}
    sx={{
      position: 'relative',
      isolation: 'isolate',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'space-between',
      textAlign: 'left',
      width: 1,
      height: 1,
      minHeight: { xs: 128, md: 208 },
      padding: { xs: 2, md: 3 },
      borderRadius: DASHBOARD_RADIUS,
      color: 'common.white',
      background: TONES[tone].background,
      boxShadow: CARD_SHADOW,
      '&::before': {
        content: '""',
        position: 'absolute',
        zIndex: -1,
        right: -72,
        bottom: -96,
        width: 280,
        height: 280,
        borderRadius: '50%',
        border: '40px solid rgba(255, 255, 255, 0.06)'
      },
      '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 3 },
      '@media (prefers-reduced-motion: no-preference)': {
        transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        '& .tile-arrow, & .tile-icon': { transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)' },
        '&:hover': { transform: 'translateY(-2px)' },
        '&:hover .tile-arrow': { transform: 'translate(2px, -2px)' },
        '&:hover .tile-icon': { transform: 'scale(1.06)' },
        '&:active': { transform: 'translateY(0) scale(0.99)' }
      },
      '&:hover': { boxShadow: TONES[tone].hoverShadow },
      ...sx
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" width={1}>
      <Box
        className="tile-icon"
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: { xs: 44, md: 56 },
          height: { xs: 44, md: 56 },
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.14)',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.18)'
        }}
      >
        {icon}
      </Box>
      <ArrowOutwardIcon className="tile-arrow" sx={{ opacity: 0.85 }} />
    </Stack>
    <Stack spacing={0.5} sx={{ mt: { xs: 2, md: 4 } }}>
      <Typography
        component="span"
        sx={{ fontSize: { xs: '1.5rem', md: '1.875rem' }, fontWeight: 600, lineHeight: 1.1 }}
      >
        {title}
      </Typography>
      <Typography component="span" sx={{ fontSize: '0.9rem', opacity: 0.82 }}>
        {description}
      </Typography>
    </Stack>
  </ButtonBase>
);
